import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { AuthError, login as authLogin } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

// Mejora "Login en LicitAI" del changelog cliente:
// el formulario vive dentro del SPA y habla con /api/v1/auth/login en
// lugar de redirigir a la pantalla nativa de Keycloak. El backend
// reenvía las credenciales a Keycloak vía Direct Access Grants.

/**
 * URL del flujo "olvidé mi contraseña" de Keycloak.
 *
 * Mejora "Recuperación de contraseña" del changelog cliente: en lugar
 * de reimplementar token+email+pantalla nosotros, abrimos el flujo
 * nativo de Keycloak. El usuario sale brevemente a Keycloak para pedir
 * el email y, al pulsar el enlace del correo, define la nueva
 * contraseña; al terminar, Keycloak redirige de vuelta al SPA.
 *
 * Implementación: link directo a `/login-actions/reset-credentials`.
 * Es el mismo endpoint que el botón "Forgot password?" del login
 * nativo de Keycloak. Probé antes `kc_action=RESET_CREDENTIALS` en el
 * auth endpoint pero Keycloak 23 NO lo trata como action válida (solo
 * acepta UPDATE_PASSWORD, delete_account, CONFIGURE_TOTP…), así que
 * caía al login normal.
 *
 * Requisitos en Keycloak (ver docker/keycloak/SETUP.md):
 *  - Realm Settings → Login → Forgot password: ON
 *    (sin esto el endpoint devuelve 400).
 *  - Realm Settings → Email: SMTP configurado. En dev apunta a MailHog
 *    vía docker-compose; en producción al SMTP de Meridianos.
 */
function buildForgotPasswordUrl(): string {
  const base = (
    import.meta.env.VITE_KEYCLOAK_URL || "http://localhost:8080"
  ).replace(/\/+$/, "");
  const realm = import.meta.env.VITE_KEYCLOAK_REALM || "licitai";
  const clientId =
    import.meta.env.VITE_KEYCLOAK_CLIENT_ID || "licitai-frontend";
  const params = new URLSearchParams({ client_id: clientId });
  return `${base}/realms/${realm}/login-actions/reset-credentials?${params}`;
}

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const callbackUrl = searchParams.get("callbackUrl") || "/";

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setErrorMessage(null);
    setSubmitting(true);
    try {
      await authLogin(username.trim(), password);
      navigate(callbackUrl, { replace: true });
    } catch (err) {
      if (err instanceof AuthError) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage("Error de red al iniciar sesión. Inténtalo de nuevo.");
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 relative overflow-hidden bg-ink">
      {/* Sistema de diseño Meridianos: hero oscuro con acento circular teal. */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
        style={{
          background: "linear-gradient(160deg, #1c1c1c 0%, #002e30 100%)",
        }}
      />
      <div
        className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full pointer-events-none"
        aria-hidden
        style={{
          border: "1px solid rgba(0, 153, 160, 0.4)",
          boxShadow: "0 0 0 40px rgba(0, 153, 160, 0.06)",
        }}
      />

      <Card className="w-full max-w-sm bg-surface border-line shadow-card relative z-10">
        <CardHeader className="text-center">
          <img
            src="/logo-meridianos.png"
            alt="Meridianos"
            className="mx-auto mb-4 h-10 w-auto object-contain"
            style={{ filter: "invert(1)" }}
          />
          <CardTitle className="font-heading text-2xl text-ink">
            LicitAI
          </CardTitle>
          <CardDescription className="text-content-muted">
            Inicia sesión para acceder a la plataforma
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {errorMessage && (
              <div
                className="flex items-start gap-2 rounded border-l-4 border-danger bg-danger/8 p-3 text-sm text-danger"
                role="alert"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <label
                htmlFor="username"
                className="block text-xs font-heading font-semibold text-ink uppercase tracking-wider"
              >
                Usuario o email
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={submitting}
                className="w-full px-3 py-2 rounded border border-line bg-surface text-sm text-content placeholder:text-content-muted focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 disabled:opacity-60"
              />
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="password"
                className="block text-xs font-heading font-semibold text-ink uppercase tracking-wider"
              >
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={submitting}
                  className="w-full px-3 py-2 pr-10 rounded border border-line bg-surface text-sm text-content placeholder:text-content-muted focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  className="absolute inset-y-0 right-0 grid place-items-center px-3 text-content-muted hover:text-ink"
                  aria-label={
                    showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                  }
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              <div className="pt-0.5 text-right">
                <a
                  href={buildForgotPasswordUrl()}
                  className="text-xs font-heading font-semibold text-brand hover:text-brand-hover underline-offset-2 hover:underline"
                >
                  ¿Has olvidado tu contraseña?
                </a>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-brand hover:bg-brand-hover border border-brand-border text-white font-heading font-semibold rounded"
              size="lg"
              disabled={submitting || !username.trim() || !password}
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {submitting ? "Iniciando sesión…" : "Iniciar sesión"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
